import React, { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import MCPServiceCard from '@/components/settings/MCPServiceCard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { MCPService, SubscriptionTier } from '@/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMCPServices } from '@/hooks/supabase/useMCPServices';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const MCPServices = () => {
  const { 
    services, 
    loading, 
    error,
    addService, 
    updateService, 
    removeService, 
    getApiKey, 
    setApiKey,
    isServiceActivated,
    refreshServices
  } = useMCPServices();
  const { user } = useAuth();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | 'all'>('all');
  const [newService, setNewService] = useState<Partial<MCPService>>({
    name: '',
    description: '',
    url: '',
    icon: 'cpu',
    tier: 'standard',
  });
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filteredServices = services.filter(service => {
    const matchesSearch = searchQuery 
      ? service.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    
    const matchesTier = tierFilter !== 'all' ? service.tier === tierFilter : true;
    
    return matchesSearch && matchesTier;
  });

  const tierCounts = {
    all: services.length,
    free: services.filter(s => s.tier === 'free').length,
    standard: services.filter(s => s.tier === 'standard').length,
    enterprise: services.filter(s => s.tier === 'enterprise').length,
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshServices();
      toast({
        title: "Refreshed",
        description: "MCP services refreshed successfully",
      });
    } catch (err) {
      console.error("Error refreshing services:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.name || !newService.url) {
      toast({
        title: "Missing information",
        description: "Please provide a name and URL for the service",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await addService(newService as Omit<MCPService, "id">);
      setIsAddDialogOpen(false);
      setNewService({
        name: '',
        description: '',
        url: '',
        icon: 'cpu',
        tier: 'standard',
      });

      toast({
        title: "Service added",
        description: "Your MCP service has been added successfully",
      });
    } catch (error) {
      console.error('Error adding service:', error);
      toast({
        title: "Error",
        description: "Failed to add MCP service",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateService = async (service: MCPService) => {
    try {
      await updateService(service.id, service);
      
      toast({
        title: "Service updated",
        description: "Your MCP service has been updated successfully",
      });
    } catch (error) {
      console.error('Error updating service:', error);
      toast({
        title: "Error",
        description: "Failed to update MCP service",
        variant: "destructive",
      });
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      await removeService(serviceId);
      
      toast({
        title: "Service removed",
        description: "Your MCP service has been removed successfully",
      });
    } catch (error) {
      console.error('Error removing service:', error);
      toast({
        title: "Error",
        description: "Failed to remove MCP service",
        variant: "destructive",
      });
    }
  };

  const handleSaveApiKey = async (serviceId: string, apiKey: string) => {
    try {
      await setApiKey(serviceId, apiKey);
      
      toast({
        title: "API key saved",
        description: "Your API key has been saved successfully",
      });
      
      return true;
    } catch (error) {
      console.error('Error saving API key:', error);
      toast({
        title: "Error",
        description: "Failed to save API key",
        variant: "destructive",
      });
      return false;
    }
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl font-bold">Please log in</h1>
          <p className="mt-2">You need to be logged in to manage MCP services</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">MCP Services</h1>
            <p className="text-muted-foreground">
              Manage your Machine-Callable Program (MCP) services to extend AI capabilities
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleRefresh} 
              disabled={isRefreshing || loading}
            >
              {isRefreshing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw size={16} className="mr-2" />
              )}
              Refresh
            </Button>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus size={16} className="mr-2" />
              Add Service
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Service Unavailable</AlertTitle>
            <AlertDescription>
              MCP services could not be loaded. Please try again later.
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-2" 
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={tierFilter}
            onValueChange={(value) => setTierFilter(value as SubscriptionTier | 'all')}
          >
            <SelectTrigger className="w-full md:w-44">
              <SelectValue placeholder="Filter by tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers ({tierCounts.all})</SelectItem>
              <SelectItem value="free">Free ({tierCounts.free})</SelectItem>
              <SelectItem value="standard">Basic ({tierCounts.standard})</SelectItem>
              <SelectItem value="enterprise">Enterprise ({tierCounts.enterprise})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading && !error ? (
          <div className="flex justify-center items-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading services...</span>
          </div>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Services</TabsTrigger>
              <TabsTrigger value="free">Free</TabsTrigger>
              <TabsTrigger value="standard">Standard</TabsTrigger>
              <TabsTrigger value="enterprise">Enterprise</TabsTrigger>
            </TabsList>
            
            {['all', 'standard', 'enterprise'].map((tab) => (
              <TabsContent key={tab} value={tab} className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredServices
                    .filter(service => tab === 'all' || service.tier === tab)
                    .map((service) => (
                      <MCPServiceCard
                        key={service.id}
                        service={service}
                        apiKey={getApiKey(service.id)}
                        onUpdate={handleUpdateService}
                        onDelete={handleDeleteService}
                        onSaveApiKey={(key) => handleSaveApiKey(service.id, key)}
                        isActivated={isServiceActivated(service.id)}
                      />
                  ))}

                  {filteredServices.filter(service => tab === 'all' || service.tier === tab).length === 0 && (
                    <div className="col-span-full text-center p-12 border rounded-lg bg-card">
                      <h3 className="text-lg font-medium mb-2">No matching MCP services found</h3>
                      <p className="text-muted-foreground mb-4">
                        {searchQuery ? 'Try adjusting your search or filters' : 'Add a new MCP service to get started'}
                      </p>
                      <Button onClick={() => setIsAddDialogOpen(true)}>
                        <Plus size={16} className="mr-2" />
                        Add Service
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add MCP Service</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  placeholder="e.g., Weather API"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  placeholder="Describe what this service does"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="url">Service URL</Label>
                <Input
                  id="url"
                  value={newService.url}
                  onChange={(e) => setNewService({ ...newService, url: e.target.value })}
                  placeholder="https://api.example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key (Optional)</Label>
                <Input
                  id="apiKey"
                  value={newService.apiKey || ''}
                  onChange={(e) => setNewService({ ...newService, apiKey: e.target.value })}
                  placeholder="Your API key"
                  type="password"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <Select
                  value={newService.icon || 'cpu'}
                  onValueChange={(value) => setNewService({ ...newService, icon: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cpu">CPU</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="code">Code</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="calculator">Calculator</SelectItem>
                    <SelectItem value="search">Search</SelectItem>
                    <SelectItem value="languages">Languages</SelectItem>
                    <SelectItem value="bar-chart">Bar Chart</SelectItem>
                    <SelectItem value="github">GitHub</SelectItem>
                    <SelectItem value="trending-up">Trending Up</SelectItem>
                    <SelectItem value="mic">Microphone</SelectItem>
                    <SelectItem value="file-text">File Text</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tier">Subscription Tier</Label>
                <Select
                  value={newService.tier as string}
                  onValueChange={(value) => setNewService({ ...newService, tier: value as SubscriptionTier })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsAddDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleAddService} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Service'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default MCPServices;
